import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Label } from '../../lib/shadcn/label'
import { Textarea } from '../../lib/shadcn/textarea'
import { toast } from '../../lib/shadcn/sonner'
import { useGetFaireSettings, useUpdateFaireSettings } from '../../hooks/backend/settingsPage'
import { HelpText } from '../../components/HelpText'

interface Loaded {
  wholesale_cents: number
  retail_cents: number
  premium_wholesale_cents: number
  premium_retail_cents: number
  system_prompt: string
  instruction: string
  default_system_prompt: string
  default_instruction: string
}

// Prices are edited in dollars and saved in cents.
interface Draft {
  wholesale: string
  retail: string
  premium_wholesale: string
  premium_retail: string
  system_prompt: string
  instruction: string
}

const dollars = (cents: number) => (cents / 100).toFixed(2)

const toCents = (value: string): number | null => {
  const n = Number(value.replace(/[$,\s]/g, ''))
  return Number.isFinite(n) && n > 0 ? Math.round(n * 100) : null
}

const PRICE_FIELDS: { key: keyof Draft; label: string }[] = [
  { key: 'wholesale', label: 'Wholesale' },
  { key: 'retail', label: 'Retail (MSRP)' },
  { key: 'premium_wholesale', label: 'Premium wholesale' },
  { key: 'premium_retail', label: 'Premium retail (MSRP)' },
]

// Settings > Sales channels > Faire: what the Hub sends to Faire when a product with
// Faire ticked under Publish To is saved.
export default function FaireSettings() {
  const getSettings = useGetFaireSettings()
  const updateSettings = useUpdateFaireSettings()
  const data = getSettings.data as Loaded | null | undefined

  const [draft, setDraft] = useState<Draft | null>(null)

  useEffect(() => {
    void getSettings.trigger({}, { skipCache: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (data) {
      setDraft({
        wholesale: dollars(data.wholesale_cents),
        retail: dollars(data.retail_cents),
        premium_wholesale: dollars(data.premium_wholesale_cents),
        premium_retail: dollars(data.premium_retail_cents),
        system_prompt: data.system_prompt,
        instruction: data.instruction,
      })
    }
  }, [data])

  const patch = (p: Partial<Draft>) => setDraft(d => (d ? { ...d, ...p } : d))

  const save = async () => {
    if (!draft) return
    const cents = {
      wholesale_cents: toCents(draft.wholesale),
      retail_cents: toCents(draft.retail),
      premium_wholesale_cents: toCents(draft.premium_wholesale),
      premium_retail_cents: toCents(draft.premium_retail),
    }
    if (Object.values(cents).some(v => v == null)) {
      toast.error('Enter every price as a dollar amount above $0')
      return
    }
    if (!draft.system_prompt.trim() || !draft.instruction.trim()) {
      toast.error('Fill in both prompts')
      return
    }
    try {
      await updateSettings.trigger({
        ...(cents as Record<keyof typeof cents, number>),
        system_prompt: draft.system_prompt.trim(),
        instruction: draft.instruction.trim(),
      })
      await getSettings.trigger({}, { skipCache: true })
      toast.success('Faire settings saved')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save Faire settings')
    }
  }

  return (
    <Card id="faire">
      <CardHeader>
        <CardTitle>Faire</CardTitle>
        <CardDescription><HelpText>
          Saving a product with Faire ticked under Publish To sends its Small bag to Faire. Faire
          gets the price below, case size 6, the flavor's tier minimum from Wholesale Tier
          Minimums, and a description written by ChatGPT. Faire can take a few minutes to show a
          change.
        </HelpText></CardDescription>
      </CardHeader>
      <CardContent>
        {getSettings.error && !data ? (
          <p className="text-sm text-destructive">Couldn't load Faire settings.</p>
        ) : !draft || !data ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : (
          <div className="space-y-4">
            <div className="space-y-1.5">
              <p className="text-sm font-medium">Prices per bag</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {PRICE_FIELDS.map(f => (
                  <div key={f.key} className="space-y-1">
                    <Label htmlFor={`faire-${f.key}`} className="text-xs text-muted-foreground">
                      {f.label}
                    </Label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                        $
                      </span>
                      <Input
                        id={`faire-${f.key}`}
                        inputMode="decimal"
                        className="h-9 pl-6"
                        value={draft[f.key]}
                        onChange={e => patch({ [f.key]: e.target.value } as Partial<Draft>)}
                      />
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Premium prices apply to flavors with a category containing "Premium".
              </p>
            </div>

            <PromptField
              id="faire-system-prompt"
              label="AI instructions"
              hint="Who ChatGPT is, the brand voice and the rules for both descriptions. It must ask for JSON with full_description and short_description."
              rows={10}
              value={draft.system_prompt}
              defaultValue={data.default_system_prompt}
              onChange={v => patch({ system_prompt: v })}
            />

            <PromptField
              id="faire-instruction"
              label="Product prompt"
              hint="Sent for each product. {label_name} and {description} are filled in from the product."
              rows={8}
              value={draft.instruction}
              defaultValue={data.default_instruction}
              onChange={v => patch({ instruction: v })}
            />

            <div className="flex justify-end">
              <Button
                type="button"
                onClick={() => void save()}
                disabled={updateSettings.loading}
                className="gap-1.5"
              >
                {updateSettings.loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Save
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

function PromptField({
  id,
  label,
  hint,
  rows,
  value,
  defaultValue,
  onChange,
}: {
  id: string
  label: string
  hint: string
  rows: number
  value: string
  defaultValue: string
  onChange: (value: string) => void
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={id}>{label}</Label>
        {value.trim() !== defaultValue.trim() && (
          <button
            type="button"
            className="text-xs text-muted-foreground underline-offset-2 hover:underline"
            onClick={() => onChange(defaultValue)}
          >
            Reset to default
          </button>
        )}
      </div>
      <Textarea id={id} rows={rows} value={value} onChange={e => onChange(e.target.value)} />
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  )
}
